import { useEffect, useMemo, useState } from "react";
import { Loader2 } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useCart } from "../context/CartContext";

export const isBuyer = (user) => !!user && ["member", "guest"].includes(user.role);

/** Logged-in shoppers: live final prices from /ticketing/checkout/preview (same as checkout). */
export function useCartPreview(enabled) {
  const { user: authUser, authAxios } = useAuth();
  const user = isBuyer(authUser) ? authUser : null;
  const { items, packages } = useCart();
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const payload = useMemo(() => ({
    items: items.map(i => ({ ticket_type_id: i.ticket_type_id, quantity: i.quantity, variant_id: i.variant_id || null })),
    packages: packages.map(p => ({ package_id: p.package_id, quantity: p.quantity, variant_selections: p.variant_selections || undefined })),
  }), [items, packages]);
  const key = JSON.stringify(payload);

  useEffect(() => {
    if (!enabled || !user || (payload.items.length + payload.packages.length) === 0) { setPreview(null); return; }
    let cancelled = false;
    setLoading(true);
    const t = setTimeout(() => {
      authAxios.post("/ticketing/checkout/preview", payload)
        .then(r => { if (!cancelled) setPreview(r.data); })
        .catch(() => { if (!cancelled) setPreview(null); })
        .finally(() => { if (!cancelled) setLoading(false); });
    }, 300);
    return () => { cancelled = true; clearTimeout(t); };
  }, [enabled, user, key]); // eslint-disable-line react-hooks/exhaustive-deps

  return { preview, loading, loggedIn: !!user };
}

const eur = (v) => `€${Number(v || 0).toFixed(2)}`;

export function CartPriceLines({ preview, loading }) {
  if (!preview && loading) return <div className="py-3 text-center text-slate-400"><Loader2 className="w-4 h-4 animate-spin inline" /></div>;
  if (!preview?.lines?.length) return null;
  return (
    <div className="mt-2 rounded-xl border border-emerald-200 bg-emerald-50/50 p-3 space-y-2" data-testid="cart-final-prices">
      <p className="text-[11px] font-bold uppercase tracking-wide text-emerald-700">Your prices</p>
      {preview.lines.map((ln, idx) => (
        <div key={`${ln.label}-${idx}`} className="text-xs border-b border-emerald-100 last:border-b-0 pb-1.5 last:pb-0" data-testid={`cart-final-line-${idx}`}>
          <div className="flex justify-between gap-2">
            <span className={`font-medium min-w-0 break-words ${ln.is_member_price ? "text-[#2d8a4e]" : "text-[#1e3a5f]"}`}>{ln.label}</span>
            <span className="font-bold text-slate-800 tabular-nums">{eur(ln.subtotal)}</span>
          </div>
          <div className="flex justify-between text-[11px] text-slate-500">
            <span>{ln.quantity} × {eur(ln.unit_price)}{ln.discount_amount > 0 && <span className="line-through ml-1 text-slate-400">{eur(ln.base_unit_price)}</span>}</span>
            {ln.discount_amount > 0 && <span className="text-[#2d8a4e]">−{eur(ln.discount_amount * ln.quantity)} {ln.discount_label}</span>}
          </div>
        </div>
      ))}
    </div>
  );
}
